import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, memo } from 'react'
import TokenSocials from './TokenSocials'
import BackToTop from './BackToTop'
import useTokenDescriptions from './hooks/useTokenDescriptions'
import { getDexPaid, onDexStatus, boostedMints } from '../utils/dexStatus'
import { normalizeTokenImage } from '../utils/jupiter'
import { WS_URL } from '../utils/chartUtils'

/* ── ScanSelect ──────────────────────────────────────────────────────────
   Replaces the native <select>. A <select> delegates its popup to the OS,
   so on Android the category and interval menus opened the platform picker
   — full-screen, system-styled, ignoring the terminal's look entirely, and
   nothing here can restyle it. Rendering our own list keeps the menu inside
   the app on every platform, and matches the dropdown language already used
   matching the dropdown language of the AVWAP and Value Area menus, so this
   introduces no new visual vocabulary.

   Groups are plain labelled sections rather than <optgroup>, which only has
   meaning to a native select. Closes on outside pointer-down and Escape. */
function ScanSelect({ value, groups, onChange, minWidth }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const flat = useMemo(() => groups.flatMap(g => g.options), [groups])
  const current = flat.find(o => o.value === value)
  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className="scan-select-wrap" ref={wrapRef} style={minWidth ? { minWidth } : undefined}>
      <button
        type="button"
        className="scan-select-btn"
        onClick={() => setOpen(o => !o)}
      >
        <span className="scan-select-label">{current ? current.label : ''}</span>
        <span className="caret">▾</span>
      </button>
      {open && (
        <div className="scan-select-dd">
          {groups.map((g, gi) => (
            <div key={g.label || gi}>
              {g.label && <div className="scan-select-group">{g.label}</div>}
              {g.options.map(o => (
                <button
                  key={o.value}
                  type="button"
                  className={`scan-select-option${o.value === value ? ' selected' : ''}`}
                  onClick={() => { setOpen(false); if (o.value !== value) onChange(o.value) }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const MAP_SRC_GROUPS = [{ label: 'Map', options: [{ value: 'hl', label: 'Futures' }, { value: 'sol', label: 'Solana' }] }]   // daily-vol-20261020
const SCAN_CATEGORY_GROUPS = [
  { label: 'Jupiter', options: [
    { value: 'toptrending',      label: 'Trending' },
    { value: 'toptraded',        label: 'Top Traded' },
    { value: 'toporganicscore',  label: 'Organic' },
  ] },
  { label: 'Live Stream', options: [
    { value: 'live_volume',      label: '⚡ Volume' },
    { value: 'live_delta',       label: '⚡ Delta' },
    { value: 'live_trades',      label: '⚡ Trades' },
    { value: 'live_organic',     label: '⚡ Organic' },
    // launch scan (launchscan-20261027) — replaces the old CHDR-decay Fresh:
    // pump.fun launches graded at 3m / 5m, top ~10% listed.
    { value: 'live_fresh',       label: 'Fresh' },
    // retail+churn scan — top-5% green 1m bars where real buyers (churn and
    // routers removed) net bought; FAKE = heavy green bar with no real buying.
    { value: 'live_retail_churn', label: 'Retail+Churn' },
    // ignition scan — coins < 24h igniting off a dead base (5m surge vs prior 30m)
    { value: 'live_ignition',    label: 'Ignition' },
  ] },
  { label: 'Boosts', options: [
    { value: 'boost_latest',     label: '🚀 DEX Paid — Latest' },
    { value: 'boost_top',        label: '🚀 DEX Paid — Top' },
  ] },
]

const SCAN_INTERVAL_GROUPS = [
  { label: '', options: [
    { value: '5m',  label: '5m' },
    { value: '1h',  label: '1h' },
    { value: '6h',  label: '6h' },
    { value: '24h', label: '24h' },
  ] },
]

// ─── Icon normalization at the DOOR ───
// Four payloads enter this panel unnormalized (REST scan poll, live scan WS,
// boosts snapshot, boost_new) and only searchTokens — the header search box —
// ever ran normalizeTokenImage. Everything here went straight from Jupiter /
// the boost feed to an <img src>.
//
// Normalizing at the door rather than at each <img> is deliberate: scanTokens
// is mirrored up to App via onTickerFeed and rendered by the chart's ticker
// marquee, which draws [...tickerTokens, ...tickerTokens] — every token TWICE
// — in a continuously animating bar. A URL parse per icon per render lands in
// exactly the layout/style churn the perf trace already flagged. This runs
// once per payload instead.
//
// Identity-preserving: a list with no IPFS icons returns its own objects, so
// memoized rows don't re-render just because the payload passed through here.
const healIcon = (t) => {
  if (!t) return t
  const icon = t.icon ? normalizeTokenImage(t.icon) : t.icon
  const logo = t.logoURI ? normalizeTokenImage(t.logoURI) : t.logoURI
  if (icon === t.icon && logo === t.logoURI) return t
  return { ...t, icon, logoURI: logo }
}
const healIcons = (list) => (Array.isArray(list) ? list.map(healIcon) : [])

// Copy-address button for scan cards. Sits right after the name in every
// card variant (organic, boosts, trending). Two-page icon -> check on
// success; stopPropagation so copying never opens the token underneath.
function CopyMint({ mint }) {
  const [done, setDone] = useState(false)
  if (!mint) return null
  return (
    <button
      className={`jup-scan-copy${done ? ' done' : ''}`}
      title="Copy address"
      aria-label="Copy address"
      onClick={async (e) => {
        e.stopPropagation()
        e.preventDefault()
        try {
          await navigator.clipboard.writeText(mint)
          setDone(true)
          setTimeout(() => setDone(false), 1200)
        } catch {}
      }}
    >
      {done ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  )
}


/**
 * ScanPanel — token scanner in its own workspace cell.
 *
 * Extracted from FootprintChartCanvas. Three modes, one <select>:
 *   Jupiter  — tokens/v2 REST poll every 30s (toptrending/toptraded/organic)
 *   Live     — own WebSocket to the data server, scan_subscribe stream
 *   Boosts   — DexScreener boost feed broadcast on App's MAIN data WS
 *
 * Mount = open: Workspace only renders panels present in the tree, so the
 * old `tapeTab !== 'scan'` gates became plain mount-lifetime effects — the
 * live socket closes and the poll stops when the cell is closed.
 *
 * The bottom ticker tape STAYS in the chart (chart-resident UI). While the
 * Ticker toggle here is on, scanTokens are reported up via onTickerFeed and
 * App mirrors them into the chart's `tickerTokens` prop; off/unmounted →
 * onTickerFeed(null) and scan updates never touch the chart.
 *
 * The chart keeps its own boost listener for toasts + the messages-log
 * mirror (global behavior); this panel listens only for the feed snapshots.
 *
 * Props:
 *   wsRef        — ref to App's main data WebSocket (boost feed lives there)
 *   wsStatus     — connection status; listener effects key on it so they
 *                  re-attach after a reconnect (refs alone never re-fire)
 *   onSelect     — (mint) => void, charts the token
 *   onTickerFeed — (tokens|null) => void, mirrors scanTokens for the chart ticker
 */


// ============================================
// ── Cross-sectional flow alignment: the "correlation" of the scanned board ──
// This panel holds no price series, so pairwise return correlation is not
// computable from the scan feed. What every scan_update DOES carry is each
// token's live buy/sell flow, and the cross-sectional statistic
// |sum(delta)| / sum(|delta|) is an honest correlation-family measure of it:
//   1.0 -> every token's net flow shares a sign. The whole board is being
//          bought, or dumped, TOGETHER — one correlated move.
//   0.0 -> flows are mixed. Idiosyncratic churn, each token on its own.
// Volume-weighted by construction, so size moves the needle more than count.
//
// Reads _raw.delta first — the server's own figure, the same one the cards
// print — and falls back to buy-sell so it still works if delta is absent.
function computeFlowAlignment(tokens) {
  let net = 0, gross = 0, up = 0, counted = 0
  for (const t of tokens || []) {
    const raw = t._raw || {}
    const b = raw.buyVolume  ?? t.stats24h?.buyVolume  ?? 0
    const sv = raw.sellVolume ?? t.stats24h?.sellVolume ?? 0
    const d = typeof raw.delta === 'number' ? raw.delta : (b - sv)
    if (b + sv <= 0 && d === 0) continue
    counted++; net += d; gross += Math.abs(d)
    if (d > 0) up++
  }
  if (counted < 3 || gross <= 0) return null   // two tokens are not a market
  return { alignment: Math.abs(net) / gross, dir: net >= 0 ? 1 : -1,
           breadth: up / counted, n: counted }
}

function FlowAlignmentBar({ tokens }) {
  const a = useMemo(() => computeFlowAlignment(tokens), [tokens])
  if (!a) return null
  const pct = Math.round(a.alignment * 100)
  const green = a.dir > 0
  const color = green ? 'var(--buy-color, #26a69a)' : 'var(--sell-color, #ef5350)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 6px' }}
         title={`Flow alignment across ${a.n} scanned tokens.\n${pct}% of net flow shares one direction — high = the board moving as one (correlated ${green ? 'bid' : 'dump'}), low = idiosyncratic.\nBreadth: ${Math.round(a.breadth * 100)}% of tokens net-bought.`}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                     color: 'var(--text-dim, #888)', whiteSpace: 'nowrap' }}>MKT SYNC</span>
      <div style={{ flex: 1, height: 6, borderRadius: 3,
                    background: 'var(--panel-border, #22262e)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3,
                      transition: 'width 400ms ease, background 400ms ease' }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color, whiteSpace: 'nowrap',
                     fontVariantNumeric: 'tabular-nums' }}>{pct}% {green ? '\u25B2' : '\u25BC'}</span>
    </div>
  )
}

// ChdrChip — OUR organic score, from cheddar_organic.py's wallet classifier.
//
// A SEPARATE chip from ORG, deliberately, and it is not a styling choice:
//   • different scale — ours is 0-100, the live scan's `organic` is ~0-10.
//   • different question — the live figure reads the current burst; ours
//     asks how much of the last 24h came from wallets that behave like
//     people, judged on 14 days of cross-token history.
//   • different provenance — ours is computed here, from our own tape, by
//     a classifier we can audit. Jupiter's is a poll. Merging them into one
//     number would make a disagreement between them invisible, and the
//     disagreement is the interesting part.
// Nothing is rendered when the field is absent, so this is inert until the
// server starts publishing `chdr` — deploy order doesn't matter.
// ── AuthChip: mint / freeze authority ────────────────────────────────────
// A live mint authority means the SUPPLY IS NOT FIXED, which is a different
// claim from "scam". On a four-minute-old memecoin it is the rug vector —
// the dev can print into your bid, and every mcap-keyed number on this
// terminal is quoting a denominator they control. On JitoSOL it is how
// staking works. Same fact, opposite meaning, and the difference is age.
//
// So the server hides the young ones outright and sends the established ones
// through WITH the reason attached; this chip is where that reason lands.
// Green is a positive verification, not an absence of bad news: both
// authorities are revoked and, on Solana, revocation is irreversible — that
// is a permanent property of the token, not a snapshot.
//
// Nothing renders when `auth` is absent, which covers both "not checked yet"
// and the gate's degraded mode. An unverified token must never wear a badge
// that says it was verified.
const AuthChip = memo(function AuthChip({ auth, why }) {
  if (auth === 'ok') {
    return (
      <div className="jup-scan-organic tier-high" style={{ opacity: 0.75 }}
           title="Mint and freeze authority both revoked — supply is fixed and your tokens cannot be frozen. Irreversible on Solana, so this is permanent.">
        <div className="jup-scan-organic-value" style={{ fontSize: '0.85em' }}>✓</div>
        <div className="jup-scan-organic-label">AUTH</div>
      </div>
    )
  }
  if (auth === 'flag') {
    return (
      <div className="jup-scan-organic tier-low"
           title={`${why || 'authority live'} — the issuer can still change this token's supply or freeze holders. Normal for LSTs, bridges and stablecoins; a rug vector on a new launch. New tokens with this flag are hidden from the scan entirely.`}>
        <div className="jup-scan-organic-value" style={{ fontSize: '0.85em' }}>⚠</div>
        <div className="jup-scan-organic-label">AUTH</div>
      </div>
    )
  }
  return null
})

const ChdrChip = memo(function ChdrChip({ score, watching, why }) {
  const scored = typeof score === 'number' && Number.isFinite(score)
  // WATCHED but not yet scored. Distinct from both a score and from nothing:
  // the engine gated this token seconds ago and is still judging its wallets.
  // That state is worth its own presentation because "we are studying this
  // one" is the early signal the tiering exists to surface — showing it as a
  // plain dash would make a freshly-gated token look like one nobody looked
  // at, which is the opposite of true.
  if (!scored && watching) {
    return (
      <div className="jup-scan-organic tier-mid" style={{ opacity: 0.8 }}
           title={`Watching — promoted for full analysis${why ? ` (${why})` : ''}. Score lands once enough wallets are judged.`}>
        <div className="jup-scan-organic-value" style={{ fontSize: '0.85em' }}>◉</div>
        <div className="jup-scan-organic-label">WATCH</div>
      </div>
    )
  }
  // Pending: not gated, so nothing has looked at it closely. A dash, not a
  // zero — zero is a real and damning verdict.
  if (!scored) {
    return (
      <div className="jup-scan-organic tier-low" style={{ opacity: 0.28 }}
           title="Not promoted for analysis yet">
        <div className="jup-scan-organic-value">–</div>
        <div className="jup-scan-organic-label">CHDR</div>
      </div>
    )
  }
  // Our own 0-100 anchors. Deliberately NOT Jupiter's 70/40: theirs ranks a
  // token against the whole ecosystem, ours measures the share of activity
  // from wallets our classifier judged organic. Same name, different
  // question — so the same number does not mean the same thing.
  const tier = score >= 60 ? 'tier-high' : score >= 30 ? 'tier-mid' : 'tier-low'
  return (
    <div className={`jup-scan-organic ${tier}`}
         title={`CHDR ${score}/100 — share of judged volume from wallets classified organic${why ? ` · gated on ${why}` : ''}`}>
      <div className="jup-scan-organic-value">{Math.round(score)}</div>
      <div className="jup-scan-organic-label">CHDR</div>
    </div>
  )
})

// dormant scan — shared chip style for the dormant row
const LG_F = (F) => (F === 300 ? '5m' : F === 120 ? '2m' : '3m')
const DORM_CHIP = { padding: '0 4px', borderRadius: 3, border: '1px solid #2a2a35', color: '#9ab', background: '#16161a', whiteSpace: 'nowrap' }

// scan vol — ATR% + range/ATR chips (SparkCache 30m 1m-bars, dormant-style TR)
function ScanVolChips({ atrPct, rangePct, rangeAtr, titlePrefix = '30m GT 1m bars' }) {
  if (!(typeof atrPct === 'number' && atrPct > 0)) return null
  const atrLabel = atrPct >= 0.01
    ? `${(atrPct * 100).toFixed(1)}% ATR`
    : `${(atrPct * 100).toFixed(2)}% ATR`
  const rngTitle = (typeof rangePct === 'number')
    ? ` · window range ${(rangePct * 100).toFixed(2)}%`
    : ''
  return (
    <div className="jup-scan-dorm-row"
         style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
      <span className="jup-scan-dorm-chip" style={DORM_CHIP}
            title={`${titlePrefix}: mean true-range scaled to the window${rngTitle}`}>
        {atrLabel}
      </span>
      {typeof rangeAtr === 'number' && rangeAtr > 0 && (
        <span className="jup-scan-dorm-chip" style={DORM_CHIP}
              title={`window true range${rngTitle} vs ATR ${(atrPct * 100).toFixed(2)}%`}>
          {rangeAtr.toFixed(2)} ATR
        </span>
      )}
    </div>
  )
}

// LiveScanCard — per-card live scan render (sparklines removed)
const LiveScanCard = memo(function LiveScanCard({ token, index, onSelect, description, boosted, currentToken }) {
  const addr   = token.address || token.id || ''
  const isActive = !!(currentToken && addr && addr === currentToken)
  const raw    = token._raw || {}
  // The server's old 0-10 `organic` heuristic is retired. It scored the
  // current window only — no wallet history — so it could not tell a first
  // buyer from a wallet that had sniped four hundred launches. Worse, its
  // buy/sell "balance" term rewarded wash traders (who alternate sides) and
  // penalised the genuine one-way demand that makes a token worth finding.
  // `chdr` replaces it: same intent, computed from 14 days of cross-token
  // wallet behaviour by cheddar_organic.py.
  const score  = typeof raw.chdr === 'number' ? raw.chdr : null
  const watched = !!raw.chdr_watch
  const tier   = score == null ? 'tier-low'
               : score >= 60 ? 'tier-high' : score >= 30 ? 'tier-mid' : 'tier-low'
  const name   = token.symbol || token.name || `${addr.slice(0, 6)}…`
  const delta  = raw.delta || 0
  const buyV   = raw.buyVolume || 0
  const sellV  = raw.sellVolume || 0

  const fmtSol   = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toFixed(1)
  // Age since the mint's first recorded trade in the capture archive.
  // `firstSeen` only rides the wire once the server has the TRUE birth from
  // disk — absent means "not looked up yet", so render nothing rather than
  // a guess. HH:MM under a day ("00:10" = ten minutes old), days after
  // that, and "7d+" at the archive's retention floor: a token older than
  // the archive has no knowable age here, and the plus says so.
  const fmtBirth = (fsMs) => {
    if (!Number.isFinite(fsMs) || fsMs <= 0) return null
    const ms = Date.now() - fsMs
    if (ms < 0) return null
    const d = ms / 86400000
    if (d >= 6.9) return '7d+'
    if (d >= 1)   return `${Math.floor(d)}d`
    const h = Math.floor(ms / 3600000)
    const m = Math.floor((ms % 3600000) / 60000)
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }
  // dormant scan — `createdAt` is the TRUE birth (DexScreener pairCreatedAt /
  // GeckoTerminal pool_created_at). When present it has no archive floor, so
  // show real days instead of capping at "7d+".
  const fmtTrueAge = (msBirth) => {
    if (!Number.isFinite(msBirth) || msBirth <= 0) return null
    const ms = Date.now() - msBirth
    if (ms < 0) return null
    const d = ms / 86400000
    if (d >= 1) return `${Math.floor(d)}d`
    const h = Math.floor(ms / 3600000)
    const m = Math.floor((ms % 3600000) / 60000)
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }
  const trueAgeStr = fmtTrueAge(raw.createdAt)
  const ageStr = trueAgeStr || fmtBirth(raw.firstSeen)
  // dormant scan — chips, only on rows the dormant scan produced.
  const isDorm = typeof raw.dormScore === 'number'
  // ignition scan — chips only on rows the ignition scan produced
  const isIgn = typeof raw.ignFiredAt === 'number'
  // retail+churn scan — chips only on rows the retail+churn scan produced
  const isRc = typeof raw.rcBarAt === 'number'
  // launch scan — chips only on rows the launch grader produced (Fresh tab)
  const isLg = typeof raw.lgGrade === 'number'
  const winMin = raw.dormWin || 5
  const vaLabel = { above: 'above VAH', below: 'below VAL', edge_high: 'edge VAH', edge_low: 'edge VAL' }[raw.dormVaPos] || null
  const fmtDelta = (v) => {
    const a = Math.abs(v)
    return a >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toFixed(1)
  }

  return (
    <div
      className={`jup-scan-row ${tier}${isActive ? ' active' : ''}`}
      onClick={() => onSelect(addr)}
      title={addr}
      // Left edge marks a WATCHED token — one the engine promoted for full
      // analysis. Inline rather than a class because it is a single property
      // and adding a stylesheet rule for it would put the state's definition
      // in a different file from the flag that drives it. Subtle on purpose:
      // this is a "look here" for scrolling, not an alert.
      // Inset child bar (not box-shadow) so it stops short of neighbours.
    >
      {watched && <i className="jup-scan-watch-mark" aria-hidden="true" />}
      <div className="jup-scan-head">
        <span className="jup-scan-rank">{index + 1}</span>
        {(token.icon || token.logoURI)
          ? <img
              className="jup-scan-logo"
              src={token.icon || token.logoURI}
              alt=""
              loading="lazy"
              onError={(e) => { e.target.style.visibility = 'hidden' }}
            />
          : <div className="jup-scan-logo jup-scan-logo-placeholder" />}
        <span className="jup-scan-name">{name}</span>
        <CopyMint mint={addr} />
        <ChdrChip score={score} watching={raw.chdr_watch} why={raw.chdr_why} />
        <AuthChip auth={raw.auth} why={raw.authWhy} />
        <DexChips mint={addr} boosted={boosted} />
      </div>
      <div className="jup-scan-price-row">
        <span className={`jup-scan-change ${delta >= 0 ? 'up' : 'down'}`}>
          Δ {delta >= 0 ? '+' : ''}{fmtDelta(delta)}
        </span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#888' }}
              title={ageStr ? (trueAgeStr ? 'trades · wallets · token age (first pool created)' : 'trades · wallets · age since first recorded trade') : 'trades · wallets'}>
          {raw.trades || 0}t · {raw.wallets || 0}w{ageStr ? ` · ${ageStr}` : ''}
        </span>
      </div>
      {isDorm && (
        /* dormant scan — effort vs result, location, and who is on each side */
        <div className="jup-scan-dorm-row"
             style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
          {raw.dormLane && (
            /* dormant scan lane: 'absorb' (volume without range) | 'wake' (demand-led) */
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: raw.dormLane === 'wake' ? '#e0a040' : '#9ab' }}
                  title={raw.dormLane === 'wake'
                    ? `wake lane: one-sided buying into an old coin's pool${raw.dormFuel != null ? ` - net buy ${Math.round(raw.dormFuel * 100)}% of pool SOL` : ''}${raw.dormPoolSol ? ` (${Math.round(raw.dormPoolSol)} SOL)` : ''}${raw.dormMoveRatio != null ? ` - move ${Number(raw.dormMoveRatio).toFixed(2)}x the curve's` : ''}${raw.dormBoosts ? ` - ${raw.dormBoosts} boost${raw.dormBoosts > 1 ? 's' : ''} (wallet surge / big buy)` : ''}`
                    : 'absorb lane: volume without range, outside value, a few buyers absorbing sellers'}>
              {raw.dormLane}
            </span>
          )}
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`window volume vs normal ${winMin}m volume over the prior week (median)${raw.dormBaseDays != null ? ` · baseline ${raw.dormBaseDays}d` : ''}`}>
            {Number(raw.dormRelVol || 0).toFixed(1)}x vol
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`window true range ${((raw.dormRangePct || 0) * 100).toFixed(2)}% vs ATR ${((raw.dormAtrPct || 0) * 100).toFixed(2)}% (prior week, same resolution)`}>
            {Number(raw.dormRangeAtr || 0).toFixed(2)} ATR
          </span>
          {vaLabel && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: raw.dormVaPos === 'above' || raw.dormVaPos === 'edge_high' ? '#26a69a' : '#e0a040' }}
                  title={`price vs the prior week's 70% value area${raw.dormVaDist ? ` · ${Number(raw.dormVaDist).toFixed(2)} VA widths outside` : ''}`}>
              {vaLabel}
            </span>
          )}
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`distinct buyer vs seller wallets in ${winMin}m · net buy ${Math.round((raw.dormNetFrac || 0) * 100)}% of volume · top buyers carry ${Math.round((raw.dormTopShare || 0) * 100)}% of buys · ${raw.dormRepeatBuyers || 0} repeat buyers`}>
            {raw.dormBuyers || 0}b / {raw.dormSellers || 0}s · +{Math.round((raw.dormNetFrac || 0) * 100)}%
          </span>
          {raw.dormLiq === 'unknown' && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, opacity: 0.6 }}
                  title="no previous liquidity snapshot yet — liquidity-pull filter could not be applied">liq ?</span>
          )}
          {raw.dormFresh === false && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, opacity: 0.6 }}
                  title="stopped firing recently; held briefly so it does not flicker">cooling</span>
          )}
        </div>
      )}
      {isIgn && (
        /* ignition scan — mcap at fire, surge vs base, breadth, move, tags */
        <div className="jup-scan-dorm-row"
             style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
          <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#e0a040' }}
                title={`fired ${new Date(raw.ignFiredAt).toLocaleTimeString()} · coin age ${Math.round(raw.ignAgeM || 0)}m${raw.ignCurve ? ' · on bonding curve' : ''}`}>
            {'\u{1F525}'} {Math.max(0, Math.round((Date.now() - raw.ignFiredAt) / 60000))}m ago
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP} title="market cap at last fire">
            ${(raw.ignMcap || 0) >= 1000 ? `${((raw.ignMcap || 0) / 1000).toFixed(1)}k` : Math.round(raw.ignMcap || 0)}
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP} title="5m organic volume vs median 5m volume of the prior 30m">
            {Number(raw.ignMult || 0).toFixed(1)}x vol
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`distinct buyers in 5m (wash + launch snipers excluded) · organic net buy ${Math.round((raw.ignNet || 0) * 100)}% · prior 20m range ${Math.round((raw.ignBaseRange || 0) * 100)}%`}>
            {raw.ignBuyers || 0}b · +{Math.round((raw.ignNet || 0) * 100)}%
          </span>
          <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#26a69a' }} title="price change over the 5m window">
            +{Math.round((raw.ignChg || 0) * 100)}%
          </span>
          {(raw.ignTags || []).includes('whales') && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#448aff' }}
                  title="3+ wallets new to this coin each bought 1+ SOL in the window while on the curve">whales</span>
          )}
          {raw.ignFresh === false && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, opacity: 0.6 }}
                  title="not firing right now; listed for 30m after the last fire">cooling</span>
          )}
        </div>
      )}
      {isRc && (
        /* retail+churn scan — hit time, volume vs normal, real net, churn, value tag */
        <div className="jup-scan-dorm-row"
             style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
          {raw.rcKind === 'fake' ? (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#ef5350', borderColor: '#ef5350' }}
                  title="FAKE VOLUME: top-5% green 1m bar but real buyers did not net buy (churn / routers stepped the price up). Warning, not a buy.">
              {'\u26A0'} FAKE VOL
            </span>
          ) : (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#26a69a' }}
                  title="top-5% green 1m bar with real buyers net buying (churn and routers removed)">
              REAL BUY
            </span>
          )}
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`1m bar ${new Date(raw.rcBarAt).toLocaleTimeString()} · listed 30m`}>
            {new Date(raw.rcBarAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {Math.max(0, Math.round((Date.now() - raw.rcBarAt) / 60000))}m ago
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`bar volume ${Number(raw.rcVol || 0).toFixed(1)} SOL vs median 1m bar of the prior 90m`}>
            {raw.rcVolX != null ? `${Number(raw.rcVolX).toFixed(1)}x vol` : `${Number(raw.rcVol || 0).toFixed(1)} SOL`}
          </span>
          <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: (raw.rcRealNet || 0) > 0 ? '#26a69a' : '#ef5350' }}
                title="real net SOL in the bar: wallet buys minus sells after removing churn (5-10m round trips) and routers">
            real {(raw.rcRealNet || 0) >= 0 ? '+' : ''}{Number(raw.rcRealNet || 0).toFixed(1)} SOL
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`share of bar volume that is churn or router flow (router ${Math.round((raw.rcRouterPct || 0) * 100)}%)`}>
            churn {Math.round((raw.rcChurnPct || 0) * 100)}%
          </span>
          {raw.rcVa && (
            <span className="jup-scan-dorm-chip"
                  style={{ ...DORM_CHIP, color: raw.rcVa === 'ABOVE' ? '#e0a040' : raw.rcVa === 'INSIDE' ? '#26a69a' : '#9ab',
                           borderColor: raw.rcVa === 'ABOVE' ? '#e0a040' : '#2a2a35' }}
                  title={`close vs trailing 60m 70% value area · ${raw.rcVaDist != null ? `${raw.rcVaDist} VA widths from POC` : ''}${raw.rcVa === 'ABOVE' ? ' · above value has tested worse (chase)' : ''}`}>
              {raw.rcVa}{raw.rcVaDist != null ? ` ${raw.rcVaDist >= 0 ? '+' : ''}${Number(raw.rcVaDist).toFixed(1)}` : ''}
            </span>
          )}
          {raw.rcFresh === false && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, opacity: 0.6 }}
                  title="listed for 30m after the hit bar">older</span>
          )}
        </div>
      )}
      {isLg && (
        /* launch scan — grade at 2m/3m/5m (▲ = 2m top-10% passing the momentum check), top reasons, age, change since launch, mcap */
        <div className="jup-scan-dorm-row"
             style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
          <span className="jup-scan-dorm-chip"
                style={{ ...DORM_CHIP, color: raw.lgTop3 ? '#26a69a' : '#e0a040', borderColor: raw.lgTop3 ? '#26a69a' : '#2a2a35', fontWeight: raw.lgTop3 ? 700 : 400 }}
                title={`launch grade at ${LG_F(raw.lgF)}: beats ${Number(raw.lgGrade).toFixed(1)}% of launches (model p ${Number(raw.lgP || 0).toFixed(2)})${(raw.lgGrades || []).length > 1 ? ' · ' + raw.lgGrades.map(g => `${LG_F(g.F)} ${Number(g.g).toFixed(0)}`).join(' / ') : ''}. Grades which launches tend to move; not a buy signal.`}>
            {raw.lgTop3 ? 'TOP3 ' : ''}{Number(raw.lgGrade).toFixed(0)} @{LG_F(raw.lgF)}
          </span>
          {raw.lgMom && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#26a69a', opacity: 0.85 }}
                  title="top-10% at 2m and still holding up: price flat-or-up over the last 30s and under 30% off its peak. Paper-tracked; not a buy signal.">▲</span>
          )}
          <span className="jup-scan-dorm-chip" style={DORM_CHIP} title="time since the first trade">
            {Math.max(0, Math.floor((Date.now() - (raw.createdAt || 0)) / 60000))}m
          </span>
          <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: (raw.lgChg || 0) >= 0 ? '#26a69a' : '#ef5350' }}
                title="price change since the first trade (live)">
            {(raw.lgChg || 0) >= 0 ? '+' : ''}{Math.round((raw.lgChg || 0) * 100)}%
          </span>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP} title="market cap now (1B supply)">
            ${(raw.lgMcap || 0) >= 1000 ? `${((raw.lgMcap || 0) / 1000).toFixed(1)}k` : Math.round(raw.lgMcap || 0)}
          </span>
          {raw.lgMayhem && (
            <span className="jup-scan-dorm-chip" style={{ ...DORM_CHIP, color: '#448aff' }} title="pump.fun mayhem mode coin">mayhem</span>
          )}
          {(raw.lgReasons || []).slice(0, 3).map((r) => (
            <span key={r.k} className="jup-scan-dorm-chip"
                  style={{ ...DORM_CHIP, color: (r.c || 0) >= 0 ? '#9ab' : '#ef5350' }}
                  title={`${r.k}: ${(r.c || 0) >= 0 ? 'raises' : 'lowers'} the grade (${(r.c || 0) >= 0 ? '+' : ''}${r.c})`}>
              {r.t}
            </span>
          ))}
        </div>
      )}
      {!isDorm && !isIgn && !isRc && !isLg && (
        <ScanVolChips
          atrPct={typeof raw.atrPct === 'number' ? raw.atrPct : null}
          rangePct={typeof raw.rangePct === 'number' ? raw.rangePct : null}
          rangeAtr={typeof raw.rangeAtr === 'number' ? raw.rangeAtr : null}
        />
      )}
      {/* dormant already shows range/ATR; also surface ATR% as its own chip */}
      {isDorm && typeof raw.dormAtrPct === 'number' && raw.dormAtrPct > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2,
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
          <span className="jup-scan-dorm-chip" style={DORM_CHIP}
                title={`prior-week ATR ${(raw.dormAtrPct * 100).toFixed(2)}% (same resolution as range/ATR chip)`}>
            {(raw.dormAtrPct >= 0.01
              ? (raw.dormAtrPct * 100).toFixed(1)
              : (raw.dormAtrPct * 100).toFixed(2))}% ATR
          </span>
        </div>
      )}
      {(description || buyV || sellV) && (
        <div className="jup-scan-story">
          {description ? (
            <div className="jup-scan-desc" title={description}><span className="jup-scan-hl">{description}</span></div>
          ) : (
            <div className="jup-scan-story-text">
              <span className="jup-scan-hl"><strong>{fmtSol(buyV)}</strong> SOL bought, <strong>{fmtSol(sellV)}</strong> sold in {winMin}m.</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
})

// ============================================
// WatchlistCard — per-card render for the ★WL (token watchlist) tab

// ─── Squarified treemap (Bruls et al.) — compact, no library ───
// items: [{w (weight>0), ...rest}]; returns [{x,y,w,h,item}] in a 0..W/0..H box.
function squarify(items, W, H) {
  const total = items.reduce((a, b) => a + b.w, 0)
  if (!(total > 0) || !items.length) return []
  const scaled = items.map(it => ({ ...it, a: (it.w / total) * W * H })).sort((a, b) => b.a - a.a)
  const out = []
  let x = 0, y = 0, w = W, h = H, row = [], rowA = 0
  const worst = (r, ra, side) => {
    if (!r.length) return Infinity
    const s2 = ra * ra, mx = Math.max(...r.map(i => i.a)), mn = Math.min(...r.map(i => i.a))
    return Math.max((side * side * mx) / s2, s2 / (side * side * mn))
  }
  const layoutRow = () => {
    const horiz = w >= h                     // lay the row along the SHORTER side
    const side = horiz ? h : w
    const thick = rowA / side
    let off = 0
    for (const it of row) {
      const len = it.a / thick
      out.push(horiz
        ? { x, y: y + off, w: thick, h: len, item: it }
        : { x: x + off, y, w: len, h: thick, item: it })
      off += len
    }
    if (horiz) { x += thick; w -= thick } else { y += thick; h -= thick }
    row = []; rowA = 0
  }
  for (const it of scaled) {
    const side = Math.min(w, h)
    if (row.length && worst(row, rowA, side) < worst([...row, it], rowA + it.a, side)) layoutRow()
    row.push(it); rowA += it.a
  }
  if (row.length) layoutRow()
  return out
}


// ─── DEX / BOOST attribute chips ───
// Styled as .jup-scan-organic siblings so ORG · DEX · 🚀 read as one group.
// DEX = the team PAID DexScreener for a listing product (profile / ad /
// takeover). ROCKET = active boosts. Two different claims — a coin can buy a
// profile once and never boost, or boost hard with no profile — so they get
// two chips instead of one vague "promoted" badge.
function DexChips({ mint, boosted }) {
  const [, setTick] = useState(0)
  useEffect(() => onDexStatus(() => setTick(t => t + 1)), [])
  const dex = getDexPaid(mint)
  if (!dex?.paid && !boosted) return null
  return (
    <>
      {dex?.paid && (
        <div className="jup-scan-organic jup-scan-dex" title={`DexScreener paid: ${dex.types.join(', ')}`}>
          <div className="jup-scan-organic-value">✓</div>
          <div className="jup-scan-organic-label">DEX</div>
        </div>
      )}
      {boosted && (
        <div className="jup-scan-organic jup-scan-boost" title="Active DexScreener boosts">
          <div className="jup-scan-organic-value">🚀</div>
          <div className="jup-scan-organic-label">BOOST</div>
        </div>
      )}
    </>
  )
}

// ─── jup-live-20261017: Jupiter scan card (memoized) ───────────────────────
// One card per mint, keyed by mint. React.memo with a field comparison: a
// Jupiter refresh that leaves a coin at the same rank with the same data
// re-renders nothing (ScanPanel also reuses the previous token object when
// its JSON is unchanged). Live ticks only touch the cards whose mint ticked.
const agoClock = { now: Date.now(), subs: new Set(), id: 0 }
function useAgoNow() {
  const [, bump] = useState(0)
  useEffect(() => {
    agoClock.subs.add(bump)
    if (!agoClock.id) {
      agoClock.id = setInterval(() => {
        agoClock.now = Date.now()
        agoClock.subs.forEach(f => f(x => x + 1))
      }, 1000)
    }
    return () => {
      agoClock.subs.delete(bump)
      if (!agoClock.subs.size) { clearInterval(agoClock.id); agoClock.id = 0 }
    }
  }, [])
  return agoClock.now
}
const JUP_IDLE_S = 60
// 'Xs ago' off ONE shared 1s timer. Also dims its row (class on the card,
// set directly - no card re-render) once the last trade is JUP_IDLE_S old.
function JupAgo({ ts }) {
  const now = useAgoNow()
  const ref = useRef(null)
  const age = Math.max(0, Math.floor((now - ts) / 1000))
  useLayoutEffect(() => {
    const row = ref.current?.closest('.jup-scan-row')
    if (row) row.classList.toggle('jup-live-idle', age >= JUP_IDLE_S)
  })
  const lbl = age < 60 ? `${age}s` : age < 3600 ? `${Math.floor(age / 60)}m` : `${Math.floor(age / 3600)}h`
  return <span ref={ref} className="jup-live-ago" title="last trade">{lbl} ago</span>
}
const fmtLiveSol = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v >= 10 ? `${Math.round(v)}` : v.toFixed(1)
function JupLiveLine({ live }) {
  const tot = live.b5 + live.s5
  const buyPct = tot > 0 ? Math.round((live.b5 / tot) * 100) : 50
  return (
    <div className="jup-scan-live-row">
      {/* key = lastTs: only this dot remounts, replaying the pulse */}
      <span key={live.ts} className={`jup-live-dot ${live.side > 0 ? 'buy' : 'sell'}`} />
      <span title="SOL volume, last 5 min">5m {fmtLiveSol(live.v5)}◎</span>
      <span className="jup-live-bar" title={`buy ${buyPct}% / sell ${100 - buyPct}% (5m SOL)`}><i style={{ width: `${buyPct}%` }} /></span>
      <span title="trades, last 5 min">{live.n5}t</span>
      <JupAgo ts={live.ts} />
    </div>
  )
}
const _volEq = (a, b) => a === b || (!!a && !!b
  && a.atrPct === b.atrPct && a.rangePct === b.rangePct && a.rangeAtr === b.rangeAtr)
const JupScanCard = memo(function JupScanCard({ t, rank, isActive, boosted, volM, desc, live, onSelect, registerRow }) {
  const addr = t.id || t.address || ''
  const refCb = useCallback((el) => registerRow(addr, el), [addr, registerRow])
  const score = t.organicScore || 0
  const tier  = score >= 70 ? 'tier-high' : score >= 40 ? 'tier-mid' : 'tier-low'
  const ch24  = t.stats24h?.priceChange
  // jup-live: live last price when a tick has it (SOL per raw unit x 10^decimals x SOL/USD)
  const liveUsd = (live && live.px > 0 && live.sol > 0 && Number.isFinite(+t.decimals))
    ? live.px * Math.pow(10, +t.decimals) * live.sol : null
  const usd   = liveUsd || t.usdPrice
  const name  = t.symbol || t.name || `${addr.slice(0, 6)}...`

  // Price formatting — handles micro-priced memecoins with
  // subscript-zero notation: $0.0₅234
  let priceLabel = '···'
  if (usd && usd > 0) {
    if (usd >= 1)         priceLabel = '$' + usd.toFixed(usd >= 100 ? 2 : 4)
    else if (usd >= 0.01) priceLabel = '$' + usd.toFixed(5)
    else if (usd >= 1e-4) priceLabel = '$' + usd.toFixed(7)
    else {
      const s = usd.toFixed(12)
      const m = s.match(/^0\.0+/)
      if (m) {
        const zeros = m[0].length - 2
        const rest  = s.slice(m[0].length).slice(0, 4).replace(/0+$/, '') || '0'
        const sub   = '\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089'[zeros]
        priceLabel  = `$0.0${sub || `(${zeros})`}${rest}`
      } else {
        priceLabel = '$' + usd.toExponential(2)
      }
    }
  }

  // Story narrative — composed from Jupiter v2 fields when present
  const fmtAge = (createdAt) => {
    if (!createdAt) return null
    const ms = Date.now() - new Date(createdAt).getTime()
    if (ms <= 0) return null
    const h = ms / 3600000, d = h / 24
    if (h < 1)        return `${Math.max(1, Math.round(ms / 60000))}m`
    if (h < 24)       return `${Math.round(h)}h`
    if (d < 30)       return `${Math.floor(d)}d`
    if (d < 365)      return `${Math.floor(d / 30)}mo`
    return `${Math.floor(d / 365)}y`
  }
  const fmtMcap = (n) => {
    if (!n) return null
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
    return `$${Math.round(n)}`
  }
  const fmtHldrs = (n) => {
    if (!n) return null
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
    return `${n}`
  }

  const ageStr   = fmtAge(t.firstPool?.createdAt)
  const mcapStr  = fmtMcap(t.mcap || t.fdv)
  const hldrsStr = fmtHldrs(t.holderCount)
  const lp       = t.launchpad
  const tags     = Array.isArray(t.tags) ? t.tags : []
  const isVerified = tags.includes('verified')

  // Sentence 1: origin / age / launchpad
  // Sentence 2: size — mcap + holders
  // Sentence 3: momentum (only if notable: ≥+50% or ≤-30%)
  const sentences = []
  if (lp && ageStr)      sentences.push(<><strong>{ageStr}</strong>-old <strong>{lp}</strong> launch.</>)
  else if (ageStr)       sentences.push(<>Launched <strong>{ageStr}</strong> ago.</>)
  else if (lp)           sentences.push(<><strong>{lp}</strong> launch.</>)

  if (mcapStr && hldrsStr) sentences.push(<><strong>{mcapStr}</strong> mcap, <strong>{hldrsStr}</strong> holders.</>)
  else if (mcapStr)        sentences.push(<><strong>{mcapStr}</strong> mcap.</>)
  else if (hldrsStr)       sentences.push(<><strong>{hldrsStr}</strong> holders.</>)

  if (typeof ch24 === 'number') {
    if (ch24 >= 50)       sentences.push(<><span className="up">Up {Math.round(ch24)}%</span> in 24h.</>)
    else if (ch24 <= -30) sentences.push(<><span className="down">Down {Math.round(Math.abs(ch24))}%</span> in 24h.</>)
  }

  // Audit / launchpad / tag badges
  const badges = []
  if (isVerified)                          badges.push({ key: 'verif',  label: '✓ Verified', tone: 'info' })
  if (t.audit?.mintAuthorityDisabled)      badges.push({ key: 'mint',   label: '✓ Mint',     tone: 'good' })
  if (t.audit?.freezeAuthorityDisabled)    badges.push({ key: 'freeze', label: '✓ Freeze',   tone: 'good' })
  if (t.audit?.topHoldersPercentage > 30) {
    badges.push({ key: 'top', label: `${Math.round(t.audit.topHoldersPercentage)}% top`, tone: 'warn' })
  }

  return (
    <div
      ref={refCb}
      className={`jup-scan-row ${tier}${isActive ? ' active' : ''}`}
      onClick={() => onSelect(addr)}
      title={addr}
    >
      <div className="jup-scan-head">
        <span className="jup-scan-rank">{rank}</span>
        {(t.icon || t.logoURI)
          ? <img className="jup-scan-logo" src={t.icon || t.logoURI} alt="" loading="lazy" onError={(e) => { e.target.style.visibility = 'hidden' }} />
          : <div className="jup-scan-logo jup-scan-logo-placeholder" />}
        <span className="jup-scan-name">{name}</span>
        <CopyMint mint={addr} />
        {/* Socials sit on the top row right after the copy icon. Renders
            nothing when the token has no links. */}
        <TokenSocials token={t} size={12} className="jup-scan-socials" />
      </div>
      <div className="jup-scan-price-row">
        <span className={`jup-scan-price ${usd ? '' : 'pending'}`}>{priceLabel}</span>
        {typeof ch24 === 'number' && ch24 !== 0 && (
          <span className={`jup-scan-change ${ch24 >= 0 ? 'up' : 'down'}`}>
            {ch24 >= 0 ? '+' : ''}{ch24.toFixed(2)}%
          </span>
        )}
        <div className={`jup-scan-organic ${tier}`}>
          <div className="jup-scan-organic-value">{Math.round(score)}</div>
          <div className="jup-scan-organic-label">ORG</div>
        </div>
        <DexChips mint={addr} boosted={boosted} />
      </div>
      {live && <JupLiveLine live={live} />}
      {(() => {
        const vol = volM || {}
        return (
          <ScanVolChips
            atrPct={typeof vol.atrPct === 'number' ? vol.atrPct : null}
            rangePct={typeof vol.rangePct === 'number' ? vol.rangePct : null}
            rangeAtr={typeof vol.rangeAtr === 'number' ? vol.rangeAtr : null}
          />
        )
      })()}
      {(() => {
        const showDesc = !!desc
        const showStory = !showDesc && sentences.length > 0
        if (!showDesc && !showStory && badges.length === 0) return null
        return (
          <div className="jup-scan-story">
            {showDesc && (
              <div className="jup-scan-desc" title={desc}><span className="jup-scan-hl">{desc}</span></div>
            )}
            {showStory && (
              <div className="jup-scan-story-text">
                <span className="jup-scan-hl">{sentences.map((s, k) => (
                  <span key={k}>{k > 0 && ' '}{s}</span>
                ))}</span>
              </div>
            )}
            {badges.length > 0 && (
              <div className="jup-scan-badges">
                {badges.map(b => (
                  <span key={b.key} className={`jup-scan-badge ${b.tone}`}>{b.label}</span>
                ))}
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}, (a, b) => a.t === b.t && a.rank === b.rank && a.isActive === b.isActive
  && a.boosted === b.boosted && a.desc === b.desc && a.live === b.live
  && _volEq(a.volM, b.volM) && a.onSelect === b.onSelect && a.registerRow === b.registerRow)

function ScanPanel({ wsRef, wsStatus, onSelect, onSelectFut, onTickerFeed, currentToken, currentFut }) {
  // Anchor for BackToTop. The WRAP, not .scan-panel — .scan-panel is the
  // scroll port, so a button absolutely positioned inside it would scroll
  // away with the cards. The wrap doesn't move.
  const wrapRef = useRef(null)
  const [scanTokens, setScanTokens] = useState([])
  const [boostFeeds, setBoostFeeds] = useState({ latest: [], top: [] })
  const [scanVolMetrics, setScanVolMetrics] = useState({})  // scan vol — mint -> {atrPct,rangePct,rangeAtr}
  const [scanCategory, setScanCategory] = useState('toptrending')
  const [scanInterval, setScanInterval] = useState('5m')
  const [scanLoading, setScanLoading] = useState(false)
  const [showTickerTape, setShowTickerTape] = useState(false)
  const scanTimerRef = useRef(null)
  const scanWsRef = useRef(null)

  // ─── Scan mode: SOL (existing card scanners) | FUT (Hyperliquid table) ───
  // Futures coins are KNOWN entities, so the layout is a sortable TABLE,
  // not discovery cards. One metaAndAssetCtxs poll returns every listed
  // coin's mark price, 24h volume, OPEN INTEREST, funding and premium —
  // a whole-exchange scanner from a single request every 20s. OI deltas
  // are computed client-side from a per-coin ring while the panel is open
  // (HL exposes current OI only; the droplet snapshotter is the follow-up
  // for persistent history).
  // Map hover: the tile under the cursor, and its box relative to the map.
  const [mapHover, setMapHover] = useState(null)   // { r, x, y, w, h } | null
  const mapBoxRef = useRef(null)
  const [scanMode, setScanMode] = useState(() => { try { return localStorage.getItem('cheddar_scan_mode') || 'sol' } catch { return 'sol' } })
  // daily-vol-20261020: MAP source - 'hl' (Hyperliquid futures, as before) | 'sol' (Solana daily volume)
  const [mapSrc, setMapSrc] = useState(() => { try { return localStorage.getItem('solcheddar:scanMapSource') === 'sol' ? 'sol' : 'hl' } catch { return 'hl' } })
  useEffect(() => { try { localStorage.setItem('solcheddar:scanMapSource', mapSrc) } catch {} }, [mapSrc])
  const [solDaily, setSolDaily] = useState(null)   // { day, total, solUsd, coins, history }
  useEffect(() => { try { localStorage.setItem('cheddar_scan_mode', scanMode) } catch {} }, [scanMode])
  const [futRows, setFutRows] = useState([])
  const [futSort, setFutSort] = useState({ key: 'vlm', dir: -1 })
  const [futErr, setFutErr] = useState(false)
  const futOiRingRef = useRef(new Map())    // coin -> [{ts, oi}], capped

  useEffect(() => {
    if (scanMode !== 'fut' && scanMode !== 'map') return
    let dead = false
    const poll = async () => {
      try {
        // (hlxyz) Main perps + HIP-3 dexes. Add a dex name to HL_DEXES to list it.
        const HL_DEXES = ['', 'xyz']
        const parts = await Promise.all(HL_DEXES.map(dex => fetch('https://api.hyperliquid.xyz/info', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dex ? { type: 'metaAndAssetCtxs', dex } : { type: 'metaAndAssetCtxs' }),
        }).then(r => r.json()).catch(() => null)))
        if (dead || !parts[0]?.[0]?.universe) return
        const meta = { universe: [] }, ctxs = []
        for (const p of parts) {
          if (!Array.isArray(p) || !p[0]?.universe || !Array.isArray(p[1])) continue
          p[0].universe.forEach((u, k) => { if (u.isDelisted) return; meta.universe.push(u); ctxs.push(p[1][k]) })
        }
        const now = Date.now()
        const rows = meta.universe.map((u, i) => {
          const c = ctxs[i] || {}
          const px = parseFloat(c.markPx) || 0
          const prev = parseFloat(c.prevDayPx) || 0
          const oiC = parseFloat(c.openInterest) || 0
          // OI delta vs the oldest ring entry >= 25 min old (or oldest we have)
          const ring = futOiRingRef.current.get(u.name) || []
          ring.push({ ts: now, oi: oiC })
          while (ring.length > 90) ring.shift()
          futOiRingRef.current.set(u.name, ring)
          const baseEntry = ring.find(e => now - e.ts >= 25 * 60_000) || ring[0]
          const oiDelta = baseEntry && baseEntry.oi > 0 ? (oiC - baseEntry.oi) / baseEntry.oi : 0
          return {
            coin: u.name, px,
            chg: prev > 0 ? (px - prev) / prev : 0,
            vlm: parseFloat(c.dayNtlVlm) || 0,
            oiUsd: oiC * px,
            oiDelta,
            funding: parseFloat(c.funding) || 0,
            premium: parseFloat(c.premium) || 0,
          }
        }).filter(r2 => r2.px > 0)
        setFutRows(rows); setFutErr(false)
      } catch { if (!dead) setFutErr(true) }
    }
    poll()
    const t = setInterval(poll, 20_000)
    return () => { dead = true; clearInterval(t) }
  }, [scanMode])

  const futSorted = useMemo(() => {
    const { key, dir } = futSort
    return [...futRows].sort((a, b) => ((a[key] ?? 0) - (b[key] ?? 0)) * dir)
  }, [futRows, futSort])

  const isLiveScan = scanCategory.startsWith('live_')
  // Boosted mints come from the feed this panel ALREADY receives — no extra
  // network for the rocket chip, only the paid lookup costs a request.
  const boostedSet = useMemo(() => boostedMints(boostFeeds), [boostFeeds])
  const isBoostScan = scanCategory.startsWith('boost_')

  // ─── jup-live-20261017: live ticks for the Jupiter tabs ───
  // Only the Jupiter categories (trending / traded / organic). The panel
  // tells the main socket which mints are on screen (scan_live_watch); the
  // server pushes scan_tick rows for the ones that traded. Ticks land in a
  // ref and flush to state at most every 250ms. Ticks never reorder rows -
  // ranking stays the 30s REST refresh.
  const isJupScan = scanMode === 'sol' && !isLiveScan && !isBoostScan
  const [liveTicks, setLiveTicks] = useState(() => new Map())
  const tickBufRef = useRef(new Map())
  const tickTimerRef = useRef(0)
  useEffect(() => () => { if (tickTimerRef.current) clearTimeout(tickTimerRef.current) }, [])
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const selectStable = useCallback((a) => onSelectRef.current?.(a), [])
  const rowElsRef = useRef(new Map())
  const registerRow = useCallback((mint, el) => {
    if (el) rowElsRef.current.set(mint, el)
    else if (mint) rowElsRef.current.delete(mint)
  }, [])
  const jupOrderKey = isJupScan ? scanTokens.map(t => t.id || t.address || '').join(',') : ''

  // Watch set: resend when the list changes or the socket reconnects.
  useEffect(() => {
    if (!isJupScan || !jupOrderKey) return
    const ws = wsRef?.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'scan_live_watch', mints: jupOrderKey.split(',').filter(Boolean).slice(0, 100) }))
    }
  }, [jupOrderKey, isJupScan, wsStatus]) // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the Jupiter tabs / unmount: stop the server-side watch.
  useEffect(() => {
    if (!isJupScan) return
    return () => {
      const ws = wsRef?.current
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'scan_live_watch', mints: [] }))
    }
  }, [isJupScan]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Rank-change animation (FLIP), Jupiter tabs only ───
  // FIRST: old positions are read during render, while the DOM still shows
  // the previous order (only when the order actually changed). LAST/INVERT/
  // PLAY in a layout effect: rows whose RANK changed get translate(old-new)
  // and ease back to 0 over 200ms; new rows fade in; rows at the same rank
  // are never touched. Transform/opacity only. Skipped on the first render,
  // on a tab/interval change, and under prefers-reduced-motion. translate is
  // X+Y because the card list is a multi-column grid.
  const flipRef = useRef({ key: null, ctx: null, rank: new Map(), snap: null })
  if (isJupScan && flipRef.current.key !== jupOrderKey && flipRef.current.key !== null) {
    const snap = new Map()
    for (const [m, el] of rowElsRef.current) snap.set(m, [el.offsetLeft, el.offsetTop])
    flipRef.current.snap = snap
  }
  useLayoutEffect(() => {
    const F = flipRef.current
    if (!isJupScan) { F.key = null; F.ctx = null; F.rank = new Map(); F.snap = null; return }
    if (F.key === jupOrderKey) return
    const ctx = `${scanCategory}|${scanInterval}`
    const mints = jupOrderKey ? jupOrderKey.split(',') : []
    const rank = new Map(mints.map((m, i) => [m, i]))
    const snap = F.snap
    let reduce = false
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch {}
    const animate = F.key !== null && F.ctx === ctx && snap && !reduce
    if (animate) {
      const els = rowElsRef.current
      const moved = [], fresh = []
      for (const m of mints) {
        const el = els.get(m)
        if (!el) continue
        if (!F.rank.has(m)) { fresh.push(el); continue }
        if (F.rank.get(m) === rank.get(m)) continue        // same rank: leave it alone
        const o = snap.get(m)
        if (!o) continue
        const dx = o[0] - el.offsetLeft, dy = o[1] - el.offsetTop
        if (dx || dy) moved.push([el, dx, dy])
      }
      for (const [el, dx, dy] of moved) {
        el.style.transition = 'none'
        el.style.transform = `translate(${dx}px, ${dy}px)`
      }
      if (moved.length) moved[0][0].getBoundingClientRect()   // one flush: commit the inverted state
      for (const [el] of moved) {
        el.style.transition = 'transform 200ms ease-out'
        el.style.transform = ''
        const done = (ev) => { if (ev.target !== el) return; el.style.transition = ''; el.removeEventListener('transitionend', done) }
        el.addEventListener('transitionend', done)
      }
      for (const el of fresh) {
        el.classList.add('jup-row-enter')
        const done = (ev) => { if (ev.target !== el) return; el.classList.remove('jup-row-enter'); el.removeEventListener('animationend', done) }
        el.addEventListener('animationend', done)
      }
    }
    F.key = jupOrderKey; F.ctx = ctx; F.rank = rank; F.snap = null
  }, [jupOrderKey, isJupScan, scanCategory, scanInterval])

  // Description fetcher: feeds all three scan modes (Jupiter, Live, Boost).
  // Own hook instance — the module-level cache inside useTokenDescriptions
  // dedupes against the chart's instance (which now fetches only the
  // charted token for the watermark).
  const visibleScanMints = useMemo(() => {
    if (isBoostScan) {
      const useTop = scanCategory === 'boost_top'
      const feed = useTop ? (boostFeeds?.top || []) : (boostFeeds?.latest || [])
      return feed.map(b => b.mint).filter(Boolean)
    }
    if (isLiveScan) return scanTokens.map(t => t.address || t.mint).filter(Boolean)
    return scanTokens.map(t => t.id || t.address).filter(Boolean)
  }, [scanTokens, boostFeeds, isLiveScan, isBoostScan, scanCategory])
  const tokenDescriptions = useTokenDescriptions(visibleScanMints)

  // ─── Boost feed snapshots off the MAIN data WS ───
  // boosts_list answers our snapshot request; boost_new keeps `latest`
  // fresh between snapshots. No toasts here — the chart owns those.
  // Keyed on wsStatus, not the ref: listener effects that depend only on
  // refs never re-fire, so a reconnect would leave us attached to a dead
  // socket (same wiring lesson as the HL wallet listener).
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    const handler = (e) => {
      let msg
      try { msg = JSON.parse(e.data) } catch { return }
      if (!msg || !msg.type) return
      if (msg.type === 'daily_volume') {
        setSolDaily(msg)
      } else if (msg.type === 'boosts_list') {
        setBoostFeeds({
          latest: healIcons(msg.latest),
          top:    healIcons(msg.top),
        })
      } else if (msg.type === 'boost_new') {
        setBoostFeeds(prev => ({
          ...prev,
          latest: [healIcon(msg), ...prev.latest].slice(0, 50),
        }))
      } else if (msg.type === 'scan_vol' && msg.metrics && typeof msg.metrics === 'object') {
        // scan vol — merge ATR% / rangeATR for Jupiter rows
        setScanVolMetrics(prev => ({ ...prev, ...msg.metrics }))
      } else if (msg.type === 'scan_tick' && Array.isArray(msg.u)) {
        // jup-live: [mint, px, v1, v5, b5, s5, n1, n5, lastTs, side]
        const buf = tickBufRef.current
        for (const r of msg.u) {
          if (!Array.isArray(r) || !r[0]) continue
          buf.set(r[0], { px: r[1], v1: r[2], v5: r[3], b5: r[4], s5: r[5], n1: r[6], n5: r[7], ts: r[8], side: r[9], sol: msg.sol })
        }
        if (!tickTimerRef.current && buf.size) {
          tickTimerRef.current = setTimeout(() => {
            tickTimerRef.current = 0
            const batch = tickBufRef.current
            if (!batch.size) return
            tickBufRef.current = new Map()
            setLiveTicks(prev => {
              const next = new Map(prev)
              for (const [m, v] of batch) next.set(m, v)
              return next
            })
          }, 250)
        }
      }
    }
    ws.addEventListener('message', handler)
    return () => ws.removeEventListener('message', handler)
  }, [wsStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // daily-vol-20261020: poll the Solana daily volume aggregate while the MAP shows it.
  useEffect(() => {
    if (scanMode !== 'map' || mapSrc !== 'sol') return
    const ask = () => {
      const ws = wsRef?.current
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'daily_volume', limit: 60, days: 7 }))
    }
    ask()
    const id = setInterval(ask, 60000)
    return () => clearInterval(id)
  }, [scanMode, mapSrc, wsStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Request the cached boost snapshot when the panel mounts / WS (re)opens.
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ action: 'boosts_list' }))
  }, [wsStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Jupiter API scan (existing categories)
  useEffect(() => {
    if (isLiveScan || isBoostScan) return
    let cancelled = false
    const fetchScan = async () => {
      setScanLoading(true)
      try {
        const res = await fetch(`https://lite-api.jup.ag/tokens/v2/${scanCategory}/${scanInterval}?limit=25`)
        if (!cancelled && res.ok) {
          const json = await res.json()
          const healed = healIcons(json)
          // jup-live: keep the previous object for a coin whose data did not
          // change, so its memoized card does not re-render at all.
          setScanTokens(prev => {
            const byId = new Map()
            for (const p of prev || []) { const k = p && (p.id || p.address); if (k) byId.set(k, p) }
            let same = Array.isArray(prev) && prev.length === healed.length
            const out = healed.map((n, i) => {
              const o = byId.get(n && (n.id || n.address))
              let keep = null
              if (o) { try { if (JSON.stringify(o) === JSON.stringify(n)) keep = o } catch {} }
              const v = keep || n
              if (same && prev[i] !== v) same = false
              return v
            })
            return same ? prev : out
          })
          // scan vol — ask droplet SparkCache for ATR% on these mints
          const ws = wsRef?.current
          const mints = healed.map(t => t.id || t.address).filter(Boolean)
          if (ws && ws.readyState === WebSocket.OPEN && mints.length) {
            ws.send(JSON.stringify({ action: 'scan_vol', mints }))
            // spark fetch is async — retry once so late cache hits land
            setTimeout(() => {
              if (cancelled) return
              const w2 = wsRef?.current
              if (w2 && w2.readyState === WebSocket.OPEN) {
                w2.send(JSON.stringify({ action: 'scan_vol', mints }))
              }
            }, 4000)
          }
        }
      } catch (err) { console.warn('Scan fetch error:', err) }
      if (!cancelled) setScanLoading(false)
    }
    setScanVolMetrics({})  // fresh category/interval — drop stale ATR chips
    fetchScan()
    scanTimerRef.current = setInterval(fetchScan, 30000)
    return () => { cancelled = true; clearInterval(scanTimerRef.current) }
  }, [scanCategory, scanInterval, isLiveScan, isBoostScan]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live WS scan (live_ categories)
  useEffect(() => {
    if (!isLiveScan) {
      if (scanWsRef.current) {
        scanWsRef.current.close()
        scanWsRef.current = null
      }
      return
    }

    const sortKey = scanCategory.replace('live_', '')
    setScanLoading(true)
    // Entering live mode, DROP whatever the Jupiter effect left in
    // scanTokens. Without this, a live socket that never delivers leaves
    // the previous category's Jupiter list rendering through the LIVE row
    // template — established majors showing Δ +0.0, 0t, 0w and a dashed
    // CHDR chip, impersonating a live scan full of dead data instead of
    // showing an honestly empty panel.
    setScanTokens([])
    // Use the SAME endpoint the rest of the app talks to. This effect used
    // to hand-build `${proto}://${window.location.hostname}:8765`, which
    // only resolves when the page is served from a host that also runs the
    // backend (local dev, Tailscale). In production the page comes from
    // Cloudflare Pages — there is no :8765 there, the socket never opened,
    // and live scan silently showed the stale list above. WS_URL already
    // encodes the real resolution order (VITE_WS_URL in prod, hostname
    // fallback in dev); we only upgrade the scheme for https pages, since
    // a ws:// constructor throws a SYNCHRONOUS SecurityError there (mixed
    // content) — uncaught inside the effect it took the whole app down.
    const wsUrl = window.location.protocol === 'https:'
      ? WS_URL.replace(/^ws:/, 'wss:')
      : WS_URL
    let ws
    try {
      ws = new WebSocket(wsUrl)
    } catch (err) {
      console.warn('[Scan] live scan socket failed to open:', err)
      setScanLoading(false)
      return
    }
    scanWsRef.current = ws

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'scan_subscribe', sort: sortKey }))
      setScanLoading(false)
    }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        if (msg.type === 'scan_update' && Array.isArray(msg.tokens)) {
          setScanTokens(msg.tokens.map(t => ({
            address: t.mint,
            name: t.name || (t.mint ? t.mint.slice(0, 6) + '...' : '?'),
            symbol: t.symbol || '',
            logoURI: normalizeTokenImage(t.logoURI || ''),
            stats24h: {
              buyVolume: t.buyVolume || 0,
              sellVolume: t.sellVolume || 0,
            },
            _raw: {
              ...t,
              trades: t.tradeCount || 0,
              wallets: t.uniqueWallets || 0,
            },
          })))
        }
      } catch {}
    }

    ws.onerror = () => setScanLoading(false)
    ws.onclose = () => setScanLoading(false)

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'scan_unsubscribe' }))
      }
      ws.close()
      scanWsRef.current = null
    }
  }, [scanCategory, isLiveScan])

  // ─── Ticker feed mirror ───
  // Report scanTokens up only while the toggle is on; clear on toggle-off
  // and on unmount so the chart's ticker disappears with the panel.
  // Fresh scan: the ticker shows only CHDR · AUTH ✓ · 🚀, so stamp what the
  // chart can't know on its own (boost status lives here, not in chart-only
  // windows) and flag the mode.
  const tickerFeed = useMemo(() => {
    if (!showTickerTape || scanTokens.length === 0) return null
    if (scanCategory !== 'live_fresh') return scanTokens
    return scanTokens.map(t => ({ ...t, _fresh: true, _boosted: boostedSet.has(t.address || t.id || '') }))
  }, [showTickerTape, scanTokens, scanCategory, boostedSet])
  useEffect(() => {
    if (!onTickerFeed) return
    onTickerFeed(tickerFeed)
    return () => onTickerFeed(null)
  }, [tickerFeed, onTickerFeed])

  return (
    <div className="scan-panel-wrap" ref={wrapRef} style={{ position: 'relative' }}>
      <style>{`
        /* ── ScanSelect ── in-app replacement for the native <select>.
           Trigger deliberately mimics the old select's footprint so the
           toolbar row doesn't reflow. */
        .scan-select-wrap { position: relative; display: inline-block; flex: 0 0 auto; }
        .scan-select-btn {
          display: flex; align-items: center; justify-content: space-between; gap: 4px;
          width: 100%; background: #16161a; border: 1px solid #2a2a35; border-radius: 4px;
          color: #ccc; font-size: 11px; font-weight: 600; font-family: inherit;
          padding: 3px 6px; cursor: pointer; transition: all 0.1s; line-height: 1.4;
          white-space: nowrap;
        }
        .scan-select-btn:hover { background: #1c1c22; border-color: #3a3a48; color: #eee; }
        .scan-select-label { overflow: hidden; text-overflow: ellipsis; }
        .scan-select-btn .caret { font-size: 8px; opacity: 0.5; flex: 0 0 auto; }
        /* Popup can exceed the trigger; cap and scroll so a long group list
           never runs off a short viewport (phone landscape especially). */
        /* Defined here, not borrowed: .avwap-dropdown lives inside the AVWAP
           and Value Area menus' own <style> blocks, so those rules only exist
           in the document while one of THOSE components is mounted. Scan can
           render without them, which would have left this popup unstyled. */
        .scan-select-dd {
          position: absolute; top: calc(100% + 4px); left: 0; z-index: 10000;
          background: #16161a; border: 1px solid #2a2a35; border-radius: 5px;
          box-shadow: 0 5px 20px rgb(0,0,0); padding: 5px; min-width: 100%;
          max-height: 60vh; overflow-y: auto;
        }
        .scan-select-option {
          display: block; width: 100%; background: none; border: none; color: #aaa;
          font-size: 11px; font-weight: 600; text-align: left; padding: 4px 10px;
          border-radius: 3px; cursor: pointer; transition: all 0.1s; white-space: nowrap;
          font-family: inherit;
        }
        .scan-select-option:hover { background: #22222a; color: #ddd; }
        .scan-select-option.selected { color: #fff; background: #2a2a3a; }
        .scan-select-group {
          font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
          color: #556; padding: 5px 10px 2px; pointer-events: none;
        }
        /* Touch: bigger hit targets than the desktop 4px rows. Keyed on the
           pointer, not a width query — a small desktop window is still a
           mouse and shouldn't get finger-sized rows. */
        @media (pointer: coarse) {
          .scan-select-btn { padding: 6px 8px; font-size: 12px; }
          .scan-select-option { padding: 9px 12px; font-size: 12px; }
        }

        /* 3D flip switch — adapted from Uiverse.io by mahiatlinux (MIT). */
        .scan3d-switch {
          font-size: 8px; /* scales the whole em-based switch to rail height */
          position: relative; display: inline-block;
          width: 5em; height: 2.5em; user-select: none; flex: 0 0 auto;
        }
        .scan3d-switch .cb { opacity: 0; width: 0; height: 0; position: absolute; }
        .scan3d-toggle {
          position: absolute; cursor: pointer; width: 100%; height: 100%;
          background-color: #1a1a1a; border-radius: 0.1em; transition: 0.4s;
          text-transform: uppercase; font-weight: 700; overflow: hidden;
          box-shadow: -0.3em 0 0 0 #1a1a1a, -0.3em 0.3em 0 0 #1a1a1a,
                      0.3em 0 0 0 #1a1a1a, 0.3em 0.3em 0 0 #1a1a1a, 0 0.3em 0 0 #1a1a1a;
        }
        .scan3d-left, .scan3d-right {
          position: absolute; display: flex; width: 50%; height: 88%;
          background-color: #2c2c2c; align-items: center; justify-content: center;
          transform-style: preserve-3d; transition: all 150ms; bottom: 0;
        }
        .scan3d-left  { color: #f3f3f3; left: 0;  transform-origin: right; transform: rotateX(10deg); }
        .scan3d-right { color: #3a3a3a; right: 1px; transform-origin: left; transform: rotateX(10deg) rotateY(-45deg); }
        .scan3d-left::before, .scan3d-right::before {
          position: absolute; content: ""; width: 100%; height: 100%; background-color: #3a3a3a;
        }
        .scan3d-left::before  { transform-origin: center left;  transform: rotateY(90deg); }
        .scan3d-right::before { transform-origin: center right; transform: rotateY(-90deg); }
        .scan3d-left::after, .scan3d-right::after {
          position: absolute; content: ""; width: 100%; height: 100%;
          background-color: #1e1e1e; transform-origin: center bottom; transform: rotateX(90deg);
        }
        .scan3d-switch input:checked + .scan3d-toggle > .scan3d-left  { transform: rotateX(10deg) rotateY(45deg); color: #3a3a3a; }
        .scan3d-switch input:checked + .scan3d-toggle > .scan3d-right { transform: rotateX(10deg) rotateY(0deg);  color: #448aff; }

        /* Matching blocky-3D dark styling for the two scan selects */
        .scan-controls select {
          appearance: none; -webkit-appearance: none;
          background-color: #2c2c2c; color: #f3f3f3;
          border: 0; border-radius: 0.1em;
          font-size: 10px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.04em; padding: 4px 18px 4px 8px; cursor: pointer;
          box-shadow: -2px 0 0 0 #1a1a1a, 2px 0 0 0 #1a1a1a,
                      0 2px 0 0 #1a1a1a, -2px 2px 0 0 #1a1a1a, 2px 2px 0 0 #1a1a1a;
          background-image: linear-gradient(45deg, transparent 50%, #448aff 50%),
                            linear-gradient(135deg, #448aff 50%, transparent 50%);
          background-position: calc(100% - 11px) center, calc(100% - 7px) center;
          background-size: 4px 4px, 4px 4px; background-repeat: no-repeat;
          transition: color 150ms, background-color 150ms;
        }
        .scan-controls select:hover { background-color: #333; }
        .scan-controls select:focus { outline: none; color: #448aff; }
        .scan-controls select optgroup, .scan-controls select option {
          background: #1a1a1a; color: #ddd; text-transform: none; font-weight: 500;
        }
      `}</style>
      <div className="panel-rail scan-panel-header">
        <span className="panel-rail-title">Scan</span>
      </div>
      <div className="scan-panel">
        <div className="scan-controls">
          <div style={{ display: 'flex', gap: 2, marginRight: 6 }}>
            {['sol', 'fut', 'map'].map(m => (
              <button key={m} onClick={() => setScanMode(m)}
                style={{
                  padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                  font: "600 10px 'JetBrains Mono', monospace", letterSpacing: '0.5px',
                  border: '1px solid ' + (scanMode === m ? '#448aff' : '#333'),
                  background: scanMode === m ? '#448aff22' : 'transparent',
                  color: scanMode === m ? '#8ab4ff' : '#888',
                }}>{m.toUpperCase()}</button>
            ))}
          </div>
          {/* Ticker tape toggle — 3D flip switch (adapted from Uiverse.io by mahiatlinux) */}
          {/* scan-hide-20261020: switch + category dropdown only on the SOL tab */}
          {scanMode === 'sol' && (<>
          <label className="scan3d-switch" title="Ticker tape">
            <input
              className="cb"
              type="checkbox"
              checked={showTickerTape}
              onChange={(e) => setShowTickerTape(e.target.checked)}
            />
            <span className="scan3d-toggle">
              <span className="scan3d-left">off</span>
              <span className="scan3d-right">on</span>
            </span>
          </label>
          <ScanSelect
            value={scanCategory}
            groups={SCAN_CATEGORY_GROUPS}
            onChange={setScanCategory}
            minWidth={132}
          />
          </>)}
          {scanMode === 'map' && (
            <ScanSelect
              value={mapSrc}
              groups={MAP_SRC_GROUPS}
              onChange={setMapSrc}
              minWidth={84}
            />
          )}
          {scanMode === 'map' && mapSrc === 'sol' && solDaily && (() => {
            const u = solDaily.solUsd || 0
            const f = (n) => n >= 1e9 ? `${(n/1e9).toFixed(2)}B` : n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : `${(n/1e3).toFixed(0)}K`
            const h = (solDaily.history || []).slice().reverse()
            const mx = Math.max(1, ...h.map(d => d.vol))
            return (
              <span title={h.map(d => `${d.day}: ${Math.round(d.vol).toLocaleString()} SOL`).join('\n')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#9aa8cc', font: "600 10px 'JetBrains Mono', monospace" }}>
                24h UTC ${f((solDaily.total || 0) * u)}
                <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 1, height: 12 }}>
                  {h.map(d => <i key={d.day} style={{ width: 3, height: Math.max(1, 12 * d.vol / mx), background: d.day === solDaily.day ? '#448aff' : '#3a4560' }} />)}
                </span>
              </span>
            )
          })()}
          {scanMode === 'sol' && !isLiveScan && (
            <ScanSelect
              value={scanInterval}
              groups={SCAN_INTERVAL_GROUPS}
              onChange={setScanInterval}
              minWidth={54}
            />
          )}
          {isLiveScan && <span style={{ color: '#448aff', fontSize: 10, fontWeight: 600 }}>{scanCategory === 'live_retail_churn' ? 'LIVE 30m' : 'LIVE 5m'}</span>}
          {scanLoading && <span className="scan-loading">...</span>}

        </div>

        {scanMode === 'sol' && (<>
        {isBoostScan ? (
          /* ── DEX/Boost scan — Jupiter-style card grid, amount chip, no spark ── */
          <div className="jup-scan-list">
            {(() => {
              const useTop = scanCategory === 'boost_top'
              const feed = useTop ? boostFeeds.top : boostFeeds.latest
              if (feed.length === 0) {
                return <div className="watchlist-empty">No boosts loaded yet</div>
              }
              return feed.map((b, i) => {
                const addr = b.mint
                const desc = tokenDescriptions.get(addr)
                const amt  = b.amount
                const amtLabel = amt >= 1e6
                  ? `$${(amt / 1e6).toFixed(1)}M`
                  : amt >= 1e3
                    ? `$${(amt / 1e3).toFixed(1)}K`
                    : `$${Math.round(amt)}`
                const name = b.symbol || b.name || `${addr.slice(0, 6)}…${addr.slice(-4)}`
                const isActive = !!(currentToken && addr && addr === currentToken)
                return (
                  <div
                    key={`${addr}_${amt}_${i}`}
                    className={`jup-scan-row${isActive ? ' active' : ''}`}
                    onClick={() => onSelect(addr)}
                    title={addr}
                  >
                    <div className="jup-scan-head">
                      <span className="jup-scan-rank">{i + 1}</span>
                      {b.icon
                        ? <img className="jup-scan-logo" src={b.icon} alt="" loading="lazy" onError={(e) => { e.target.style.visibility = 'hidden' }} />
                        : <div className="jup-scan-logo jup-scan-logo-placeholder" />}
                      <span className="jup-scan-name">{name}</span>
                      <CopyMint mint={addr} />
                      <div className="jup-scan-amount">
                        <div className="jup-scan-amount-value">{amtLabel}</div>
                        <div className="jup-scan-amount-label">BOOST</div>
                      </div>
                    </div>
                    {desc && (
                      <div className="jup-scan-story">
                        <div className="jup-scan-desc" title={desc}><span className="jup-scan-hl">{desc}</span></div>
                      </div>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        ) : isLiveScan ? (
          /* ── Live WS scan — Jupiter card layout ── */
          <>
          <FlowAlignmentBar tokens={scanTokens} />
          <div className="jup-scan-list">
          {scanTokens.length === 0 && !scanLoading && <div className="watchlist-empty">Waiting for trades...</div>}
          {scanTokens.map((t, i) => (
            <LiveScanCard
              key={(t.address || t.id || '') + i}
              token={t}
              index={i}
              onSelect={onSelect}
              description={tokenDescriptions.get(t.address || t.id || '')}
              boosted={boostedSet.has(t.address || t.id || '')}
              currentToken={currentToken}
            />
          ))}
          </div>
          </>
        ) : (
          /* ── Jupiter tokens/v2 — vertical card grid (organic, name, price, story) ── */
          <div className="jup-scan-list">
          {scanTokens.length === 0 && !scanLoading && <div className="watchlist-empty">No tokens found</div>}
          {scanTokens.map((t, i) => {
            const addr = t.id || t.address || ''
            return (
              <JupScanCard
                key={addr || i}
                t={t}
                rank={i + 1}
                isActive={!!(currentToken && addr && addr === currentToken)}
                boosted={boostedSet.has(addr)}
                volM={scanVolMetrics[addr]}
                desc={tokenDescriptions.get(addr)}
                live={liveTicks.get(addr)}
                onSelect={selectStable}
                registerRow={registerRow}
              />
            )
          })}
          </div>
        )}
        </>)}

      {scanMode === 'map' && (
        <div ref={mapBoxRef} onMouseLeave={() => setMapHover(null)}
             style={{ position: 'relative', flex: 1, minHeight: 560, overflow: 'hidden' }}>
          <style>{`@keyframes mapPop { from { transform: scale(0.88); opacity: 0.4 } to { transform: scale(1); opacity: 1 } }`}</style>
          {(() => {
            const SEC = {
              BTC:'MAJORS', ETH:'MAJORS', SOL:'MAJORS',
              DOGE:'MEMES', WIF:'MEMES', PEPE:'MEMES', BONK:'MEMES', SHIB:'MEMES', POPCAT:'MEMES', MEW:'MEMES', BRETT:'MEMES', MOG:'MEMES', FARTCOIN:'MEMES', PENGU:'MEMES', TRUMP:'MEMES', MOODENG:'MEMES', PNUT:'MEMES', GOAT:'MEMES', CHILLGUY:'MEMES', SPX:'MEMES',
              AVAX:'L1/L2', SUI:'L1/L2', APT:'L1/L2', SEI:'L1/L2', TIA:'L1/L2', NEAR:'L1/L2', TON:'L1/L2', ADA:'L1/L2', DOT:'L1/L2', ATOM:'L1/L2', ARB:'L1/L2', OP:'L1/L2', STRK:'L1/L2', ZK:'L1/L2', BLAST:'L1/L2', MNT:'L1/L2', TRX:'L1/L2', LTC:'L1/L2', BCH:'L1/L2', XRP:'L1/L2', HYPE:'L1/L2', BERA:'L1/L2', MON:'L1/L2',
              LINK:'DEFI', UNI:'DEFI', AAVE:'DEFI', MKR:'DEFI', LDO:'DEFI', CRV:'DEFI', JUP:'DEFI', JTO:'DEFI', PENDLE:'DEFI', ENA:'DEFI', DYDX:'DEFI', GMX:'DEFI', RAY:'DEFI', ONDO:'DEFI', SUSHI:'DEFI',
              FET:'AI', RENDER:'AI', TAO:'AI', WLD:'AI', AI16Z:'AI', VIRTUAL:'AI', ARC:'AI', GRIFFAIN:'AI', IO:'AI', AKT:'AI',
            }
            const H = 560                       // map height (px) — text gating math depends on it
            const HEADER = 15                   // per-region header strip (px)
            const solMap = mapSrc === 'sol'
            // daily-vol-20261020: Solana rows in the same shape the tiles read
            // (coin = label, vlm = USD day volume, chg = first->last price).
            const top = solMap
              ? (solDaily?.coins || []).map(c => ({
                  coin: c.symbol || `${c.mint.slice(0, 4)}…`, mint: c.mint, icon: c.logoURI || '',
                  vlm: (c.vol || 0) * (solDaily.solUsd || 0), chg: Number.isFinite(c.chg) ? c.chg : 0,
                  trades: c.trades || 0, oiUsd: 0, oiDelta: 0, funding: 0,
                  // solmap-fix-20261020: drop thin / garbage rows (old bad first prices)
                })).filter(r => r.vlm > 0 && Number.isFinite(r.vlm) && r.trades >= 20 && Number.isFinite(r.chg) && Math.abs(r.chg) <= 50)
                  .map(r => ({ ...r, chg: Math.max(-0.99, Math.min(r.chg, 50)) }))
              : [...futRows].sort((a, b) => b.vlm - a.vlm).slice(0, 60).filter(r => r.vlm > 0)
            if (!top.length) return null
            const groups = new Map()
            for (const r of top) {
              const k = solMap ? 'SOLANA' : (SEC[r.coin] || 'OTHER')
              if (!groups.has(k)) groups.set(k, [])
              groups.get(k).push(r)
            }
            const secs = [...groups.entries()].map(([name, rows]) => {
              const vlm = rows.reduce((a, r) => a + r.vlm, 0)
              return { name, rows, vlm,
                w: rows.reduce((a, r) => a + Math.sqrt(r.vlm), 0),
                chg: rows.reduce((a, r) => a + r.chg * r.vlm, 0) / Math.max(1, vlm) }
            })
            const colFor = (chg) => {
              const t = Math.max(-1, Math.min(1, chg / 0.06))
              return t >= 0
                ? `rgba(34, ${110 + 90 * t}, 74, ${0.55 + 0.35 * t})`
                : `rgba(${150 + 60 * -t}, 40, 56, ${0.55 + 0.35 * -t})`
            }
            const pctF = (x) => `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}%`
            const $v = (n) => n >= 1e9 ? `${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `${(n/1e6).toFixed(0)}M` : `${(n/1e3).toFixed(0)}K`
            // ── LEVEL 1: sectors squarified into the ONE box ──
            const regions = squarify(secs.map(x => ({ w: x.w, sec: x })), 100, 100)
            // Hover card: the hovered tile, enlarged a little (min size so
            // tiny tiles can show everything), centred on the tile and kept
            // inside the map. Drawn above the regions so their overflow clip
            // can't cut it; pointer-events none so the tile keeps the hover
            // and clicks go straight through to it.
            const hoverCard = (() => {
              if (!mapHover) return null
              const box = mapBoxRef.current
              const BW = box?.clientWidth || 340, BH = box?.clientHeight || H
              // Fresh numbers each render (the map refreshes under the hover).
              const r = top.find(x => (x.mint || x.coin) === (mapHover.r.mint || mapHover.r.coin)) || mapHover.r
              const cw = Math.min(BW, Math.max(mapHover.w * 1.12, 104))
              const ch = Math.min(BH, Math.max(mapHover.h * 1.12, 80))
              const cx = Math.max(0, Math.min(BW - cw, mapHover.x + mapHover.w / 2 - cw / 2))
              const cy = Math.max(0, Math.min(BH - ch, mapHover.y + mapHover.h / 2 - ch / 2))
              return (
                <div key={r.mint || r.coin} style={{
                  position: 'absolute', left: cx, top: cy, width: cw, height: ch, zIndex: 5,
                  // Tile colour is rgba — layer it over a solid base so the card is opaque.
                  pointerEvents: 'none', background: `linear-gradient(${colFor(r.chg)}, ${colFor(r.chg)}), #0d1017`,
                  border: '1px solid #9aa8cc', borderRadius: 4, boxSizing: 'border-box',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                  font: "600 10px 'JetBrains Mono', monospace", color: '#eef2ff',
                  textAlign: 'center', whiteSpace: 'nowrap',
                  animation: 'mapPop 120ms ease-out',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <img src={r.mint ? r.icon : `https://app.hyperliquid.xyz/coins/${r.coin}.svg`} alt=""
                      style={{ width: 15, height: 15, borderRadius: '50%' }}
                      onError={(e) => { e.currentTarget.style.display = 'none' }} />
                    <span style={{ fontSize: 11, fontWeight: 700 }}>{r.coin}</span>
                  </div>
                  <div style={{ fontSize: 10 }}>{pctF(r.chg)}</div>
                  {r.mint ? (
                    <div style={{ fontSize: 8, opacity: 0.75 }}>${$v(r.vlm)} · {r.trades} tx</div>
                  ) : (<>
                  <div style={{ fontSize: 8, opacity: 0.75 }}>${$v(r.vlm)} · OI ${$v(r.oiUsd)}</div>
                  {/* Two short lines, not one long one: on the 104px minimum
                      card the combined line wrapped and read left-aligned. */}
                  <div style={{ fontSize: 7, opacity: 0.6 }}>ΔOI {(r.oiDelta * 100).toFixed(1)}%</div>
                  <div style={{ fontSize: 7, opacity: 0.6 }}>fund {(r.funding * 100).toFixed(4)}%/h</div>
                  </>)}
                </div>
              )
            })()
            return (<>{regions.map(({ x, y, w, h, item }) => {
              const sec = item.sec
              const headPct = (HEADER / H) * 100 / (h / 100) // header as % of THIS region
              // ── LEVEL 2: coins squarified inside the region, under its header ──
              const tiles = squarify(sec.rows.map(r => ({ w: Math.sqrt(r.vlm), r })), 100, Math.max(1, 100 - headPct))
              return (
                <div key={sec.name} style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, overflow: 'hidden', outline: '1px solid #060810' }}>
                  <div style={{ height: HEADER, display: 'flex', alignItems: 'center', gap: 6, padding: '0 5px', background: '#0d1017', font: "700 9px 'JetBrains Mono', monospace", letterSpacing: '0.8px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <span style={{ color: '#9aa8cc' }}>{sec.name}</span>
                    <span style={{ color: sec.chg >= 0 ? '#4caf7d' : '#e05c6e' }}>{pctF(sec.chg)}</span>
                  </div>
                  <div style={{ position: 'relative', height: `calc(100% - ${HEADER}px)` }}>
                    {tiles.map((t) => {
                      const r = t.item.r
                      // real pixel estimate for FIT gating (conservative 340px panel width)
                      const pxW = (t.w / 100) * (w / 100) * 340
                      const pxH = (t.h / (100 - headPct + 0.0001)) * ((h / 100) * H - HEADER)
                      const showName = pxW >= 34 && pxH >= 24
                      const showIcon = pxW >= 44 && pxH >= 40
                      const showChg  = pxW >= 34 && pxH >= 34
                      const showVol  = pxW >= 64 && pxH >= 54
                      return (
                        <div key={r.mint || r.coin} onClick={() => (r.mint ? onSelect?.(r.mint) : onSelectFut?.(r.coin))}
                          onMouseEnter={(e) => {
                            const b = mapBoxRef.current?.getBoundingClientRect()
                            const tr = e.currentTarget.getBoundingClientRect()
                            if (b) setMapHover({ r, x: tr.left - b.left, y: tr.top - b.top, w: tr.width, h: tr.height })
                          }}
                          style={{
                            position: 'absolute', left: `${t.x}%`, top: `${(t.y / (100 - headPct + 0.0001)) * 100}%`,
                            width: `${t.w}%`, height: `${(t.h / (100 - headPct + 0.0001)) * 100}%`,
                            background: colFor(r.chg), border: '1px solid #0d1017', boxSizing: 'border-box',
                            cursor: 'pointer', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 1,
                            font: "600 10px 'JetBrains Mono', monospace", color: '#eef2ff',
                          }}>
                          {showIcon && (
                            <img src={r.mint ? r.icon : `https://app.hyperliquid.xyz/coins/${r.coin}.svg`} alt=""
                              style={{ width: 15, height: 15, borderRadius: '50%' }}
                              onError={(e) => { e.currentTarget.style.display = 'none' }} />
                          )}
                          {showName && <div style={{ fontSize: pxW >= 50 ? 10 : 8 }}>{r.coin}</div>}
                          {showChg && <div style={{ fontSize: 8, opacity: 0.9 }}>{pctF(r.chg)}</div>}
                          {showVol && <div style={{ fontSize: 7, opacity: 0.6 }}>${$v(r.vlm)}{r.mint ? '' : ` · OI ${$v(r.oiUsd)}`}</div>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}{hoverCard}</>)
          })()}
        </div>
      )}

      {scanMode === 'fut' && (
        <div className="fut-scan-scroll" style={{ overflow: 'auto', flex: 1, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style>{`.fut-scan-scroll::-webkit-scrollbar{display:none;width:0;height:0}`}</style>
          {futErr && <div style={{ padding: 8, color: '#e88', font: "11px 'JetBrains Mono', monospace" }}>HL fetch failed — retrying…</div>}
          <table style={{ width: '100%', maxWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', font: "11px 'JetBrains Mono', monospace" }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: '#111', zIndex: 1 }}>
                {[['coin','COIN'],['px','PRICE'],['chg','24h'],['vlm','VOLUME'],['oiUsd','OI'],['oiDelta','ΔOI 25m'],['funding','FUND/h'],['premium','PREM']].map(([k, label]) => (
                  <th key={k}
                    onClick={() => k !== 'coin' && setFutSort(s => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}
                    style={{ padding: '5px 4px', textAlign: k === 'coin' ? 'left' : 'right', color: futSort.key === k ? '#8ab4ff' : '#999', cursor: k === 'coin' ? 'default' : 'pointer', userSelect: 'none', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {label}{futSort.key === k ? (futSort.dir === -1 ? ' ▾' : ' ▴') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {futSorted.map(r => {
                const $ = (n) => n >= 1e9 ? `$${(n/1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${Math.round(n)}`
                const pct = (x, dp = 2) => `${x >= 0 ? '+' : ''}${(x * 100).toFixed(dp)}%`
                const col = (x) => x > 0 ? '#4caf7d' : x < 0 ? '#e05c6e' : '#888'
                const isActive = !!(currentFut && r.coin && r.coin.toUpperCase() === String(currentFut).toUpperCase())
                return (
                  <tr key={r.coin} onClick={() => onSelectFut?.(r.coin)}
                    className={isActive ? 'fut-scan-row active' : 'fut-scan-row'}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #1c1c1c', background: isActive ? 'rgba(240,167,43,0.06)' : undefined }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#1a1f2e' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? 'rgba(240,167,43,0.06)' : '' }}>
                    <td style={{ padding: '4px 8px', color: '#ddd', fontWeight: 600 }}>{r.coin}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: '#ccc' }}>{r.px >= 100 ? r.px.toFixed(1) : r.px >= 1 ? r.px.toFixed(3) : r.px.toPrecision(4)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: col(r.chg) }}>{pct(r.chg)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: '#aaa' }}>{$(r.vlm)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: '#aaa' }}>{$(r.oiUsd)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: col(r.oiDelta) }}>{pct(r.oiDelta, 1)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: col(r.funding) }}>{pct(r.funding, 4)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: col(r.premium) }}>{pct(r.premium)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
      <BackToTop containerRef={wrapRef} />
    </div>
  )
}

// (scrollperf) App re-renders on every X-zoom candle step (the slot's window
// state lives in App) and on every trade tick. ScanPanel's props are refs,
// strings and stable callbacks (onSelect is forwarded through a stable
// wrapper in App), so memo skips those parent renders. Its own state, feed
// listeners and useTokenDescriptions subscription still re-render it as before.
export default memo(ScanPanel)